import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /**
   * Phase 6 (R2): a cart drawer that AUTO-OPENED after a confirmed add on a
   * `product.sisterhood-ritual` page closes itself after 12s of customer
   * inactivity. Any interaction inside the drawer resets the countdown, it
   * never closes while focus is inside, and a drawer the customer opened
   * deliberately from the header is never armed. Scoped to the Sisterhood
   * ritual product template only — no other template is affected.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #autoCloseTimer = null;
  /** @type {number} */
  #autoCloseDelay = 12000;
  /** @type {boolean} */
  #autoCloseArmed = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.#disarmAutoClose();
  }

  /**
   * True only when the current page is a Sisterhood ritual product page, so
   * the auto-close is scoped to `product.sisterhood-ritual` exactly.
   * @returns {boolean}
   */
  #isSisterhoodRitualContext() {
    const el = document.querySelector('[data-template]');
    const t = el?.getAttribute('data-template') || '';
    return t.indexOf('product.sisterhood-ritual') === 0;
  }

  /** Arms the 12s inactivity auto-close for an auto-opened Sisterhood drawer. */
  #armAutoClose() {
    if (this.#autoCloseArmed) {
      this.#resetAutoCloseTimer();
      return;
    }
    if (!this.#isSisterhoodRitualContext()) return;
    this.#autoCloseArmed = true;
    this.#addAutoCloseListeners();
    this.#resetAutoCloseTimer();
  }

  #resetAutoCloseTimer() {
    if (!this.#autoCloseArmed) return;
    if (this.#autoCloseTimer) clearTimeout(this.#autoCloseTimer);
    this.#autoCloseTimer = setTimeout(() => this.#autoCloseTick(), this.#autoCloseDelay);
  }

  #autoCloseTick() {
    const drawer = this.#themeDrawer;
    const dialog = this.#dialog;
    if (!drawer?.isOpen) {
      this.#disarmAutoClose();
      return;
    }
    // Never auto-close while focus is inside the drawer. Re-check after a
    // full interval rather than relying only on a focusout event, so the
    // drawer reliably closes 12s after focus finally leaves it.
    if (dialog && dialog.contains(document.activeElement)) {
      this.#resetAutoCloseTimer();
      return;
    }
    this.#disarmAutoClose();
    drawer.close();
  }

  #handleAutoCloseInteraction = () => {
    this.#resetAutoCloseTimer();
  };

  #handleAutoCloseFocusOut = (event) => {
    const dialog = this.#dialog;
    // Focus moved entirely out of the drawer -> restart the countdown.
    if (dialog && !dialog.contains(event.relatedTarget)) this.#resetAutoCloseTimer();
  };

  #autoCloseInteractionEvents = ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart', 'input', 'change'];

  #addAutoCloseListeners() {
    const dialog = this.#dialog;
    if (!dialog) return;
    this.#autoCloseInteractionEvents.forEach((evt) =>
      dialog.addEventListener(evt, this.#handleAutoCloseInteraction, { passive: true })
    );
    dialog.addEventListener('focusin', this.#handleAutoCloseInteraction);
    dialog.addEventListener('focusout', this.#handleAutoCloseFocusOut);
  }

  #removeAutoCloseListeners() {
    const dialog = this.#dialog;
    if (!dialog) return;
    this.#autoCloseInteractionEvents.forEach((evt) =>
      dialog.removeEventListener(evt, this.#handleAutoCloseInteraction)
    );
    dialog.removeEventListener('focusin', this.#handleAutoCloseInteraction);
    dialog.removeEventListener('focusout', this.#handleAutoCloseFocusOut);
  }

  #disarmAutoClose() {
    this.#autoCloseArmed = false;
    if (this.#autoCloseTimer) clearTimeout(this.#autoCloseTimer);
    this.#autoCloseTimer = null;
    this.#removeAutoCloseListeners();
  }

  /** A deliberately closed drawer (any reason) disarms the auto-close. */
  #handleDrawerClose = () => {
    this.#disarmAutoClose();
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // If a Sisterhood drawer we already armed is still open and the customer
    // adds again, restart its 12s countdown. Guarded on #autoCloseArmed so a
    // header-opened drawer is never armed by this path.
    if (event.action === 'add' && this.#autoCloseArmed && this.#themeDrawer?.isOpen) {
      this.#resetAutoCloseTimer();
    }

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
      this.#armAutoClose();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          this.#armAutoClose();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
